/**
 * ThreatTable — DataTable + TableToolbar wrapper for Step 4.
 *
 * STRIDE filtering goes through the shared `FilterDrawer` primitive
 * (multi-select side panel, one row per STRIDE category, with counts).
 * The drawer toggle button lives in the TableToolbar; selected values
 * narrow the visible rows. Active state shows on the filter button so
 * the user knows the filter is engaged.
 *
 * Synthetic `_rowKey` mirrors ScenarioTable — threat_id collisions
 * across child TARAs need a position-stable React key.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  DataTable,
  TableToolbar,
  TablePagination,
  RowActionsMenu,
} from '../../../../components/ui/DataTable';
import FilterDrawer from '../../../../components/ui/DataTable/FilterDrawer';
import { Icon } from '../../../../components/ui/Icon';
import { Button } from '../../../../components/ui/Button2';
import {
  THREAT_COLUMNS_READ,
  THREAT_COLUMNS_EDIT,
  THREAT_ACTIONS_COLUMN_WIDTH,
  STRIDE_CATEGORIES,
  type ThreatRecord,
} from './threatFieldSchema';
import styles from './ThreatTable.module.css';

const DataTableAny = DataTable as unknown as React.ComponentType<any>;
const TableToolbarAny = TableToolbar as unknown as React.ComponentType<any>;
const TablePaginationAny = TablePagination as unknown as React.ComponentType<any>;
const RowActionsMenuAny = RowActionsMenu as unknown as React.ComponentType<any>;
const FilterDrawerAny = FilterDrawer as unknown as React.ComponentType<any>;
// Button2 is a JSX forwardRef primitive; cast for use from TSX (mirrors the
// ButtonAny convention across the step-components).
const ButtonAny = Button as unknown as React.ComponentType<any>;

const DEFAULT_PAGE_SIZE = 20;

interface SortState {
  column: string;
  direction: 'asc' | 'desc';
}

export interface ThreatTableProps {
  threats: ThreatRecord[];
  loading?: boolean;
  emptyMessage?: string;
  editable?: boolean;
  onAdd?: () => void;
  onEdit?: (threat: ThreatRecord) => void;
  onDelete?: (threat: ThreatRecord) => void;
  /** Export the current filtered/sorted threat list to CSV. */
  onExport?: () => void;
  /** Receive a parsed import file (CSV or JSON) for bulk-add. */
  onImport?: (file: File) => void;
}

function sortThreats(rows: ThreatRecord[], sort: SortState | null): ThreatRecord[] {
  if (!sort) return rows;
  const dir = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = (a as Record<string, unknown>)[sort.column];
    const bv = (b as Record<string, unknown>)[sort.column];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
    return String(av).localeCompare(String(bv)) * dir;
  });
}

function searchThreats(rows: ThreatRecord[], query: string): ThreatRecord[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) =>
    Object.values(r).some((v) =>
      v == null
        ? false
        : Array.isArray(v)
        ? v.join(' ').toLowerCase().includes(q)
        : String(v).toLowerCase().includes(q),
    ),
  );
}

export default function ThreatTable({
  threats,
  loading,
  emptyMessage = 'No threats identified yet.',
  editable = false,
  onAdd,
  onEdit,
  onDelete,
  onExport,
  onImport,
}: ThreatTableProps) {
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [strideValues, setStrideValues] = useState<string[]>([]);
  const [propertyValues, setPropertyValues] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  // Per-category counts off the unfiltered row set — drives the
  // FilterDrawer option labels ("Tampering (4)").
  const strideCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const c of STRIDE_CATEGORIES) counts[c.value] = 0;
    for (const t of threats) {
      const key = String(t.stride_category ?? '');
      if (key in counts) counts[key] += 1;
    }
    return counts;
  }, [threats]);

  // Compromised-property options + counts derived from the live row set
  // (per ISO 21434 the property is STRIDE-derived, but read it off the
  // data so the section reflects whatever values are actually present).
  const propertyOptions = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const t of threats) {
      const k = String(t.compromised_property ?? '').trim();
      if (k) counts[k] = (counts[k] ?? 0) + 1;
    }
    return Object.keys(counts).sort().map((v) => ({ value: v, label: v, count: counts[v] }));
  }, [threats]);

  const strideFiltered = useMemo(
    () => (strideValues.length === 0
      ? threats
      : threats.filter((t) => strideValues.includes(String(t.stride_category ?? '')))),
    [threats, strideValues],
  );
  const propertyFiltered = useMemo(
    () => (propertyValues.length === 0
      ? strideFiltered
      : strideFiltered.filter((t) => propertyValues.includes(String(t.compromised_property ?? '')))),
    [strideFiltered, propertyValues],
  );
  const searched = useMemo(() => searchThreats(propertyFiltered, search), [propertyFiltered, search]);
  const sorted = useMemo(() => sortThreats(searched, sort), [searched, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const paged = useMemo(
    () => sorted.slice((page - 1) * pageSize, page * pageSize),
    [sorted, page, pageSize],
  );

  // Synthetic key — child TARAs can collide on threat_id.
  const keyed = useMemo(
    () =>
      paged.map((r, i) => ({
        ...r,
        _rowKey: `${r.threat_id ?? 'row'}::${(page - 1) * pageSize + i}`,
      })),
    [paged, page, pageSize],
  );

  const columns = useMemo(() => {
    if (!editable) return THREAT_COLUMNS_READ;
    return [
      ...THREAT_COLUMNS_EDIT,
      {
        key: '_actions',
        label: '',
        align: 'right',
        width: THREAT_ACTIONS_COLUMN_WIDTH,
        sortable: false,
        render: (row: ThreatRecord) => (
          <RowActionsMenuAny
            ariaLabel={`Actions for ${row.threat_id}`}
            actions={[
              { id: 'edit', label: 'Edit', icon: <Icon name="edit" size={14} />, onClick: () => onEdit?.(row) },
              { id: 'delete', label: 'Delete', icon: <Icon name="delete" size={14} />, danger: true, onClick: () => onDelete?.(row) },
            ]}
          />
        ),
      },
    ];
  }, [editable, onEdit, onDelete]);

  const strideFilter = {
    key: 'stride_category',
    label: 'STRIDE',
    values: strideValues,
    onChange: (next: string[]) => {
      setStrideValues(next);
      setPage(1);
    },
    options: STRIDE_CATEGORIES.map((c) => ({
      value: c.value,
      label: c.label,
      count: strideCounts[c.value] ?? 0,
    })),
  };

  const propertyFilter = {
    key: 'compromised_property',
    label: 'Property',
    values: propertyValues,
    onChange: (next: string[]) => {
      setPropertyValues(next);
      setPage(1);
    },
    options: propertyOptions,
  };

  const toolbarActions = (
    <div className={styles.toolbarActions}>
      {onExport && sorted.length > 0 && (
        <ButtonAny variant="outline" size="sm" onClick={onExport}>
          <Icon name="upload" size={14} /> Export CSV
        </ButtonAny>
      )}
      {editable && onImport && (
        <>
          <ButtonAny
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            <Icon name="download" size={14} /> Import
          </ButtonAny>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.json"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onImport(f);
              if (e.target) e.target.value = '';
            }}
          />
        </>
      )}
      {editable && onAdd && (
        <ButtonAny variant="default" size="sm" onClick={onAdd}>
          <Icon name="add" size={14} /> Add Threat
        </ButtonAny>
      )}
    </div>
  );

  return (
    <div className={styles.tableShell}>
      <FilterDrawerAny
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={[strideFilter, propertyFilter]}
      />
      <DataTableAny
        columns={columns}
        data={keyed}
        rowKey="_rowKey"
        sort={sort}
        onSortChange={(column: string | null, direction: 'asc' | 'desc' | null) =>
          setSort(column && direction ? { column, direction } : null)
        }
        loading={loading}
        emptyMessage={emptyMessage}
        toolbar={
          <TableToolbarAny
            search={{
              value: search,
              onChange: (v: string) => {
                setSearch(v);
                setPage(1);
              },
              placeholder: 'Search threats…',
            }}
            count={{ value: sorted.length, label: 'Threats' }}
            onFilterToggle={() => setFilterOpen((o) => !o)}
            filterButtonActive={filterOpen || strideValues.length > 0 || propertyValues.length > 0}
            actions={toolbarActions}
          />
        }
        pagination={
          <TablePaginationAny
            currentPage={page}
            totalItems={sorted.length}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onItemsPerPageChange={(size: number) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        }
      />
    </div>
  );
}
